// CustomInput.tsx
import React, { forwardRef, useState } from 'react';
import {
  Input,
  InputProps,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  FormControl,
  FormLabel,
  Icon,
  FormErrorMessage,
} from '@chakra-ui/react';

import EyeIcon from '../../../assets/svg/eye.svg?react';
import EyeClosedIcon from '../../../assets/svg/eye-closed.svg?react';
import { FieldError } from 'react-hook-form';
import { capitalizeFirstWord } from '../../../utils/capitalizeFirstWord';
import Asterisk from '../Asterisk';

interface CustomInputProps extends InputProps {
  label?: string;
  htmlFor?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  error?: FieldError;
  bg?: string;
  required?: boolean;
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    { label, leftIcon, htmlFor, rightIcon, type = 'text', error, bg = 'white', required, ...props },
    ref, // ← now we receive the ref from Controller
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <FormControl id={htmlFor} isInvalid={!!error} variant="floating">
        {label && (
          <FormLabel
            color={error ? 'status.error' : 'text.secondary'}
            fontSize="sm"
            fontFamily="primary"
            fontWeight="normal"
            htmlFor={htmlFor}
            mb="6px"
          >
            {label} {required ? <Asterisk /> : null}
          </FormLabel>
        )}

        <InputGroup>
          {leftIcon && <InputLeftElement>{leftIcon}</InputLeftElement>}

          <Input
            ref={ref} // ← forward the ref here!
            id={htmlFor}
            type={type === 'password' && !showPassword ? 'password' : 'text'}
            sx={{ background: bg }}
            color="text.primary"
            fontFamily="primary"
            focusBorderColor="brand.primary.DEFAULT"
            borderColor="border.default"
            _focus={{
              borderColor: error ? 'status.error' : 'brand.primary',
              boxShadow: error ? '0 0 0 1px status.error' : '0 0 0 1px brand.primary',
            }}
            placeholder=" "
            _placeholder={{
              color: 'gray.400',
              fontSize: '0.875rem',
              fontFamily: 'primary',
            }}
            borderRadius="xl"
            size="md"
            paddingX="14px"
            paddingY="10px"
            {...props}
          />

          {rightIcon && <InputRightElement>{rightIcon}</InputRightElement>}

          {type === 'password' && (
            <InputRightElement onClick={togglePasswordVisibility} cursor="pointer">
              <Icon
                as={showPassword ? EyeClosedIcon : EyeIcon}
                color={error ? 'status.error' : 'text.secondary'}
              />
            </InputRightElement>
          )}
        </InputGroup>

        {error && (
          <FormErrorMessage
            fontFamily="primary"
            fontSize="xs"
            color="status.error"
            display={error.message ? 'block' : 'none'}
          >
            {capitalizeFirstWord(error.message)}.
          </FormErrorMessage>
        )}
      </FormControl>
    );
  },
);

export default CustomInput;
